import { useState, useCallback } from 'react';
import config from 'src/config';
import { useIsGridBackedSpecies } from 'src/app/portfolio/state/portfolioSpeciesSource';
import { useExplorerSiteTable } from './useSiteExplorerQueries';
import type { SiteTableRow } from '../state/api/porfolioSiteExplorerQuery';

const EMPTY_ROWS: SiteTableRow[] = [];

type Params = {
    customerId: string;
    metric?: string;
    filters?: { name: string; values: string[] }[];
    pageSize: number;
    sortBy?: { field: string; order: 'ASC' | 'DESC' };
    skip?: boolean;
};

const usePortfolioSiteTable = (params: Params) => {
    const [queryPage, setQueryPage] = useState(1);
    const isGridBacked = useIsGridBackedSpecies();

    const { currentData: pageData, isFetching: isPageFetching } = useExplorerSiteTable(
        {
            customerId: params.customerId,
            ...(params.metric ? { metric: params.metric } : {}),
            ...(params.filters?.length ? { filters: params.filters } : {}),
            pagination: { page: queryPage, pageSize: params.pageSize },
            ...(params.sortBy ? { sortBy: params.sortBy } : {}),
        },
        { skip: params.skip }
    );

    const totalCount = pageData?.pagination.total ?? 0;
    const isClientSideMode = totalCount > 0 && totalCount <= config.portfolioClientSideThreshold;
    // If total fits within a single page, the first query already has all rows — skip the second call.
    const needsAllDataFetch = isClientSideMode && totalCount > params.pageSize;

    const { currentData: allData, isFetching: isAllFetching } = useExplorerSiteTable(
        {
            customerId: params.customerId,
            ...(params.metric ? { metric: params.metric } : {}),
            ...(params.filters?.length ? { filters: params.filters } : {}),
            pagination: { page: 1, pageSize: totalCount },
        },
        { skip: !needsAllDataFetch || params.skip }
    );

    // While the all-data query is loading, fall back to the first-page rows to avoid
    // the DataTable filling its totalRowsForPagination quota with blank padding rows.
    // EMPTY_ROWS is a stable reference — avoids a new [] each render which would
    // cause an infinite re-render loop in consumers that use rows as an effect dep.
    const rows = isClientSideMode ? allData?.rows ?? pageData?.rows ?? EMPTY_ROWS : pageData?.rows ?? EMPTY_ROWS;

    // No-op in client-side mode — DataTable handles pagination locally.
    const goToPage = useCallback(
        (page: number) => {
            if (!isClientSideMode) setQueryPage(page);
        },
        [isClientSideMode]
    );

    return {
        rows,
        totalCount,
        // Always 1 in client-side mode; DataTable owns pagination from there.
        currentPage: isClientSideMode ? 1 : queryPage,
        isFetching: isPageFetching || (needsAllDataFetch && isAllFetching),
        // Pass these directly to DataTable / column defs — no isClientSideMode needed in consumers.
        isServerSideSort: !isClientSideMode,
        // Client-side mode sorts every row in the browser; the grid can be sorted on
        // the server at any size, because core merges the counts into the sort index
        // before it picks the page. The projection cannot, at any size above one page:
        // its counts are computed for the page it was already handed.
        canSortSpeciesColumns: isClientSideMode || isGridBacked,
        goToPage,
    };
};

export default usePortfolioSiteTable;
